if (typeof Header !== 'function') {
	class Header extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				header: 'header',
				headerWrapper: '.header__wrapper',
				headerBottom: '.header__bottom',
				headerPlaceholder: '.header-placeholder',
				headerObserver: '.header-observer',
				burgerMenuButton: '.burger-menu__button',
				headerMenu: '.header-menu',
				headerMenuNav: '.header-menu__nav',
				headerMenuNavPrimary: '.header-menu__nav--primary',
				headerMenuItem: '.header-menu__item',
				headerUpsell: '.header-menu__upsell',
				headerUpsellList: '.header-upsell__list',
				headerUpsellListItem: '.header-upsell__list-item',
				headerSidebarExtraTrigger: '.header-sidebar__icon--extra-trigger',
				headerSidebarCloseIcon: '.header-sidebar__icon--close',
				headerBottomNav: '.header__bottom-nav',
				headerBottomNavLeftArrow: '.header__bottom-nav-left-arrow',
				headerBottomNavRightArrow: '.header__bottom-nav-right-arrow',
				headerBottomItemHasDropdown: '.header__bottom-item--has-dropdown',
				headerBottomDropdownItemHasDropdown: '.header__bottom-dropdown-item--has-dropdown',
				headerBottomDropdown: '.header__bottom-dropdown',
				headerBottomDropdownSecondary: '.header__bottom-dropdown--secondary',
			};

			this.classes = {
				sticky: 'header--sticky',
				stickyActive: 'header--sticky-active',
				stickyActiveHidden: 'header--sticky-active-hidden',
				stickyActiveVisible: 'header--sticky-active-visible',
				headerMenuOpen: 'header-menu--open',
				headerMenuNavVisible: 'header-menu__nav--visible',
				headerMenuItemReturn: 'header-menu__item--return',
				headerMenuItemReturnIcon: 'header-menu__return-icon',
				headerUpsellVisible: 'header-menu__upsell--visible',
				headerUpsellPortrait: 'header-menu__upsell--ratio-4-3',
				headerSidebarExtra: 'header-sidebar__extra',
				headerSidebarExtraOpen: 'header-sidebar__extra--open',
				blockedScroll: 'blocked-scroll',
				headerBottomNavLeftArrowDisabled: 'header__bottom-nav-left-arrow--disabled',
				headerBottomNavRightArrowDisabled: 'header__bottom-nav-right-arrow--disabled',
				headerBottomDropdownFlipped: 'header__bottom-dropdown--flipped',
			};

			// Elements
			this.body = document.querySelector('body');
			this.header = this.querySelector(this.selectors.header);
			this.headerWrapper = this.querySelector(this.selectors.headerWrapper);
			this.headerBottom = this.querySelector(this.selectors.headerBottom);
			this.headerPlaceholder = this.querySelector(this.selectors.headerPlaceholder);
			this.headerObserver = document.querySelector(this.selectors.headerObserver);
			this.burgerMenuButton = this.querySelector(this.selectors.burgerMenuButton);
			this.headerMenu = this.querySelector(this.selectors.headerMenu);
			this.headerSidebarCloseIcon = this.querySelector(this.selectors.headerSidebarCloseIcon);
			this.headerUpsellSplide = null;
			this.headerBottomNav = this.querySelector(this.selectors.headerBottomNav);
			this.headerBottomNavLeftArrow = this.querySelector(this.selectors.headerBottomNavLeftArrow);
			this.headerBottomNavRightArrow = this.querySelector(this.selectors.headerBottomNavRightArrow);
			this.headerBottomDropdownItems = this.querySelectorAll(
				`${this.selectors.headerBottomItemHasDropdown}, ${this.selectors.headerBottomDropdownItemHasDropdown}`
			);

			// Variables
			this.headerHeight =
				this.headerWrapper.offsetHeight + (this.headerBottom ? this.headerBottom.offsetHeight : 0);
			this.isMobile = window.innerWidth < 768;

			// Flags
			this.headerIsSticky = this.header.classList.contains(this.classes.sticky);
			this.headerUpsellActive = this.dataset.upsellActive === 'true';

			// Methods
			this.init();
		}

		init() {
			this.headerIsSticky && this.stickyHeaderSetup();
			this.headerUpsellActive && this.setupUpsell();

			this.addEventListeners();
			this.setInformativeHeaderHeight();
		}

		setInformativeHeaderHeight(reset) {
			const { body, headerHeight, headerIsSticky } = this;

			const headerHeightAdjusted = headerIsSticky ? headerHeight : 0;

			body.style.setProperty('--menu-sticky-height', `${reset ? 0 : headerHeightAdjusted}px`);
		}

		addEventListeners() {
			if (this.burgerMenuButton) {
				this.burgerMenuButton.addEventListener('click', this.burgerMenuToggle.bind(this));
			}

			if (this.headerSidebarCloseIcon) {
				this.headerSidebarCloseIcon.addEventListener('click', this.burgerMenuToggle.bind(this));
			}

			this.querySelectorAll(this.selectors.headerMenuItem).forEach((child) => {
				child.addEventListener('click', this.openNavigationMenu.bind(this));
			});

			this.querySelectorAll(this.selectors.headerSidebarExtraTrigger).forEach((child) => {
				child.addEventListener('click', this.toggleSidebarExtraMenu.bind(this, child.dataset.trigger));
			});

			this.body.addEventListener('keydown', (event) => {
				if (
					this.header.classList.contains(this.classes.headerMenuOpen) &&
					(event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27)
				) {
					this.burgerMenuToggle();
				}
			});

			if (this.headerBottomNav && this.headerBottomNavLeftArrow && this.headerBottomNavRightArrow) {
				if (this.headerBottomNavLeftArrow) {
					this.headerBottomNavLeftArrow.addEventListener('click', () => this.scrollBottomNav('left'));
					this.headerBottomNavLeftArrow.addEventListener('keydown', (e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							this.scrollBottomNav('left');
						}
					});
				}
				if (this.headerBottomNavRightArrow) {
					this.headerBottomNavRightArrow.addEventListener('click', () => this.scrollBottomNav('right'));
					this.headerBottomNavRightArrow.addEventListener('keydown', (e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							this.scrollBottomNav('right');
						}
					});
				}
			}

			this.updateNavArrowsVisibility();

			if (this.headerBottomNav) {
				this.headerBottomNav.addEventListener('scroll', this.updateNavArrowsVisibility.bind(this));
			}
			window.addEventListener('resize', window.debounce(this.updateNavArrowsVisibility.bind(this), 100));

			this.headerBottomDropdownItems.forEach((item) => {
				item.addEventListener('mouseenter', this.positionDropdown.bind(this));
				item.addEventListener('focusin', this.positionDropdown.bind(this));
			});
		}

		toggleSidebarExtraMenu(trigger) {
			const { classes } = this;
			const sidebarExtraMenu = this.header.querySelector(`.${this.classes.headerSidebarExtra}--${trigger}`);

			this.header.querySelectorAll(`.${classes.headerSidebarExtra}`).forEach((child) => {
				if (child !== sidebarExtraMenu) {
					child.classList.remove(classes.headerSidebarExtraOpen);
				}
			});

			if (sidebarExtraMenu.classList.contains(classes.headerSidebarExtraOpen)) {
				sidebarExtraMenu.classList.remove(classes.headerSidebarExtraOpen);
			} else {
				sidebarExtraMenu.classList.add(classes.headerSidebarExtraOpen);
			}
		}

		setupUpsell() {
			const { selectors, isMobile, header, headerMenu } = this;
			const headerUpsell = this.querySelector(selectors.headerUpsell);
			const headerUpsellList = this.querySelector(selectors.headerUpsellList);
			const headerUpsellListItems = [...headerUpsellList.children];
			const isPortrait = headerUpsell.classList.contains(this.classes.headerUpsellPortrait);
			const listItemWidth = isMobile ? (isPortrait ? 200 : 150) : isPortrait ? 266 : 200;
			const repeatCount = Math.ceil(window.innerWidth / listItemWidth / headerUpsellList.childElementCount);
			const headerUpsellClones = Array(repeatCount).fill(headerUpsellListItems).flat();
			const headerVerticalSpacing = +headerMenu.style
				.getPropertyValue('--nav-vertical-spacing')
				.replace('px', '');

			headerUpsellList.innerHTML = '';
			headerUpsellClones.forEach((item) => headerUpsellList.appendChild(item.cloneNode(true)));
			header.style.setProperty(
				'--padding-bottom-upsell',
				`${headerUpsell.clientHeight + headerVerticalSpacing}px`
			);

			this.headerUpsellSplide = new Splide(headerUpsell, {
				type: 'loop',
				arrows: false,
				pagination: false,
				autoWidth: true,
				drag: 'free',
				focus: 'center',
				autoScroll: {
					speed: isMobile ? 0.2 : 0.5,
					autoStart: false,
				},
			});

			this.headerUpsellSplide.mount(window.splide.Extensions);
		}

		burgerMenuToggle() {
			const { body, header, headerMenu, headerHeight, classes, selectors, headerUpsellActive, headerWrapper } =
				this;
			const headerMenuExpanded = header.classList.contains(classes.headerMenuOpen);

			if (headerMenuExpanded) {
				if (headerUpsellActive) {
					this.headerUpsellSplide.Components.AutoScroll.pause();
					this.querySelector(selectors.headerUpsell).classList.remove(classes.headerUpsellVisible);
				}

				this.header.querySelectorAll(`.${this.classes.headerSidebarExtra}`).forEach((child) => {
					child.classList.remove(this.classes.headerSidebarExtraOpen);
				});

				setTimeout(() => {
					headerMenu.style.maxHeight = 0;

					this.querySelectorAll(selectors.headerMenuNav).forEach((child) => {
						child.classList.remove(classes.headerMenuNavVisible);
					});
					body.classList.remove(classes.blockedScroll);
					header.classList.remove(classes.headerMenuOpen);
					headerMenu.classList.remove(classes.headerMenuOpen);
					headerWrapper.focus();
				}, 200);
			} else {
				headerMenu.style.maxHeight = `${document.documentElement.clientHeight}px`;
				this.querySelector(selectors.headerMenuNavPrimary).classList.add(classes.headerMenuNavVisible);
				body.classList.add(classes.blockedScroll);
				header.classList.add(classes.headerMenuOpen);
				headerMenu.classList.add(classes.headerMenuOpen);

				setTimeout(() => {
					if (headerUpsellActive) {
						this.querySelector(selectors.headerUpsell).classList.add(classes.headerUpsellVisible);
						this.headerUpsellSplide.Components.AutoScroll.play();
					}
				}, 400);
			}
		}

		stickyHeaderSetup() {
			const { header, headerPlaceholder, headerObserver, classes, headerHeight } = this;
			const headerOffsetTop = headerPlaceholder.offsetTop;

			const options = {
				rootMargin: `${headerOffsetTop}px 0px 0px 0px`,
				root: null,
			};

			const optionsSecondary = {
				rootMargin: `${headerOffsetTop + 200}px 0px 0px 0px`,
				root: null,
			};

			const observerCallback = (entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						headerPlaceholder.style.height = 0;
						header.classList.remove(classes.stickyActive);
					} else {
						headerPlaceholder.style.height = `${headerHeight}px`;
						header.classList.add(classes.stickyActive);
					}
				});
			};

			const observerCallbackSecondary = (entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						header.classList.remove(classes.stickyActiveVisible);
						this.setInformativeHeaderHeight(true);
					} else {
						header.classList.add(classes.stickyActiveVisible);
						this.setInformativeHeaderHeight();
					}
				});
			};

			const observer = new IntersectionObserver(observerCallback, options);
			const observerSecondary = new IntersectionObserver(observerCallbackSecondary, optionsSecondary);

			observer.observe(headerObserver);
			observerSecondary.observe(headerObserver);
		}

		openNavigationMenu(e) {
			const { classes, selectors, headerMenu } = this;
			const isRedirect = e.currentTarget.tagName === 'A' || e.target.tagName === 'A';
			const isReturn = e.currentTarget.classList.contains(classes.headerMenuItemReturn);
			const currentParent = e.currentTarget.closest(selectors.headerMenuNav);

			currentParent.style.zIndex = 1;

			if (isRedirect) {
				return;
			}

			if (isReturn) {
				e.preventDefault();
				currentParent.parentElement.parentElement.classList.add(classes.headerMenuNavVisible);
			} else {
				const nextVisible = e.currentTarget.nextElementSibling;

				nextVisible.classList.add(classes.headerMenuNavVisible);

				if (nextVisible.dataset.level === '3') {
					e.currentTarget.closest(selectors.headerMenuNav).style.zIndex = 2;
				}
			}

			e.currentTarget.closest(selectors.headerMenuNav).classList.remove(classes.headerMenuNavVisible);
			headerMenu.scrollTop = 0;

			for (const child of this.querySelectorAll(selectors.headerMenuNav)) {
				child.scrollTop = 0;
			}
		}

		scrollBottomNav(direction) {
			const nav = this.headerBottomNav;
			if (!nav) return;

			const amount = 100;
			const value = direction === 'right' ? amount : -amount;
			nav.scrollBy({ left: value, behavior: 'smooth' });
		}

		updateNavArrowsVisibility() {
			const { headerBottomNavLeftArrowDisabled, headerBottomNavRightArrowDisabled } = this.classes;

			const nav = this.headerBottomNav;
			const leftArrow = this.headerBottomNavLeftArrow;
			const rightArrow = this.headerBottomNavRightArrow;

			if (!nav || !leftArrow || !rightArrow) return;

			const scrollLeft = nav.scrollLeft;
			const maxScroll = nav.scrollWidth - nav.offsetWidth;
			const isOverflowing = nav.scrollWidth > nav.offsetWidth + 1;

			leftArrow.style.display = rightArrow.style.display = isOverflowing ? '' : 'none';

			leftArrow.classList.toggle(headerBottomNavLeftArrowDisabled, scrollLeft <= 2);
			rightArrow.classList.toggle(headerBottomNavRightArrowDisabled, scrollLeft >= maxScroll - 2);
		}

		positionDropdown(e) {
			const { headerBottomDropdown, headerBottomDropdownItemHasDropdown } = this.selectors;
			const { headerBottomDropdownFlipped } = this.classes;

			const item = e.currentTarget;
			const dropdown = item.querySelector(headerBottomDropdown);
			if (!dropdown) return;

			const viewportWidth = this.clientWidth;
			const isSecondaryDropdown = item.matches(headerBottomDropdownItemHasDropdown);

			dropdown.style.removeProperty('right');
			dropdown.classList.remove(headerBottomDropdownFlipped);
			item.classList.remove(headerBottomDropdownFlipped);

			dropdown.style.left = isSecondaryDropdown ? '100%' : `${item.getBoundingClientRect().left}px`;

			const dropdownRect = dropdown.getBoundingClientRect();

			if (dropdownRect.right > viewportWidth) {
				if (isSecondaryDropdown) {
					dropdown.style.left = 'auto';
					dropdown.style.right = '100%';
					dropdown.classList.add(headerBottomDropdownFlipped);
					item.classList.add(headerBottomDropdownFlipped);
				} else {
					dropdown.style.left = `${Math.max(0, viewportWidth - dropdownRect.width)}px`;
				}
			}

			const innerSecondaryDropdown = item.querySelector(this.selectors.headerBottomDropdownSecondary);

			// Initialize secondary dropdown if it's not already initialized and it's overflowing the viewport
			if (
				!isSecondaryDropdown &&
				innerSecondaryDropdown &&
				innerSecondaryDropdown.getBoundingClientRect().right > viewportWidth &&
				!innerSecondaryDropdown.classList.contains(headerBottomDropdownFlipped)
			) {
				innerSecondaryDropdown.style.left = 'auto';
				innerSecondaryDropdown.style.right = '100%';
				innerSecondaryDropdown.classList.add(headerBottomDropdownFlipped);
			}
		}
	}

	if (!customElements.get('theme-header')) {
		customElements.define('theme-header', Header);
	}
}
